import countryCodeToName from '@/../public/icons/flag_svg/countries.json';

// Create reverse mapping: country name -> ISO code
const nameToCode: Record<string, string> = {};

// Build reverse mapping
Object.entries(countryCodeToName).forEach(([code, name]) => {
  nameToCode[name.toLowerCase()] = code.toLowerCase();
});

// Common country name variations and aliases
const COUNTRY_ALIASES: Record<string, string> = {
  'usa': 'us',
  'united states of america': 'us',
  'america': 'us',
  'uk': 'gb',
  'england': 'gb-eng',
  'scotland': 'gb-sct',
  'northern ireland': 'gb-nir',
  'wales': 'gb-wls',
  'south korea': 'kr',
  'korea republic of': 'kr',
  'north korea': 'kp',
  'korea democratic peoples republic of': 'kp',
  'russia': 'ru',
  'russian federation': 'ru',
  'china': 'cn',
  'peoples republic of china': 'cn',
  'taiwan': 'tw',
  'republic of china': 'tw',
  'iran': 'ir',
  'islamic republic of iran': 'ir',
  'syria': 'sy',
  'syrian arab republic': 'sy',
  'venezuela': 've',
  'bolivarian republic of venezuela': 've',
  'bolivia': 'bo',
  'plurinational state of bolivia': 'bo',
  'congo': 'cg',
  'republic of the congo': 'cg',
  'drc': 'cd',
  'democratic republic of the congo': 'cd',
  'ivory coast': 'ci',
  'côte divoire': 'ci',
  'east timor': 'tl',
  'timor-leste': 'tl',
  'macedonia': 'mk',
  'north macedonia': 'mk',
  'moldova': 'md',
  'republic of moldova': 'md',
  'myanmar': 'mm',
  'burma': 'mm',
  'palestine': 'ps',
  'turkey': 'tr',
  'republic of türkiye': 'tr',
  'vatican': 'va',
  'holy see': 'va',
  'vatican city': 'va',
  'laos': 'la',
  'lao peoples democratic republic': 'la',
  'micronesia': 'fm',
  'federated states of micronesia': 'fm',
  'vietnam': 'vn',
  'viet nam': 'vn',
  'eswatini': 'sz',
  'swaziland': 'sz',
  'kingdom of eswatini': 'sz',
  'tanzania': 'tz',
  'united republic of tanzania': 'tz',
  'brunei': 'bn',
  'brunei darussalam': 'bn',
  'cape verde': 'cv',
  'cabo verde': 'cv'
};

/**
 * Converts a country name to its ISO country code
 * Handles various name formats and common aliases
 */
export const getCountryCodeFromName = (countryName: string): string | null => {
  if (!countryName) return null;
  
  const normalized = countryName.trim().toLowerCase();
  if (!normalized) return null;
  
  // Check direct mapping first
  if (nameToCode[normalized]) {
    return nameToCode[normalized];
  }
  
  // Check aliases
  if (COUNTRY_ALIASES[normalized]) {
    return COUNTRY_ALIASES[normalized];
  }
  
  // Try partial matching for countries with long official names
  for (const [name, code] of Object.entries(nameToCode)) {
    // Match if the input is contained in the official name
    if (name.includes(normalized)) {
      return code;
    }
    // Match if the official name is contained in the input
    if (normalized.includes(name)) {
      return code;
    }
  }
  
  // Try matching individual words
  const words = normalized.split(/\s+/);
  if (words.length > 1) {
    for (const word of words) {
      if (word.length > 2) { // Ignore short words like "of", "the"
        for (const [name, code] of Object.entries(nameToCode)) {
          if (name.includes(word)) {
            return code;
          }
        }
      }
    }
  }
  
  return null;
};

/**
 * Gets the country name from ISO code
 */
export const getCountryNameFromCode = (countryCode: string): string | null => {
  if (!countryCode) return null;
  
  const code = countryCode.toUpperCase();
  return (countryCodeToName as Record<string, string>)[code] || null;
};

/**
 * Validates if a country code exists in our SVG collection
 */
export const hasCountryFlag = (countryCode: string): boolean => {
  if (!countryCode) return false;
  const code = countryCode.toLowerCase();
  return Object.keys(countryCodeToName).map(k => k.toLowerCase()).includes(code);
};

/**
 * Get country code for US states (all use US flag)
 */
export const getStateCountryCode = (stateCode: string, country?: string): string => {
  if (!stateCode) return 'us'; // Default to US
  
  // Map states to their countries
  const stateToCountry: Record<string, string> = {
    // US states (common abbreviations)
    'al': 'us', 'ak': 'us', 'az': 'us', 'ar': 'us', 'ca': 'us', 'co': 'us',
    'ct': 'us', 'de': 'us', 'fl': 'us', 'ga': 'us', 'hi': 'us', 'id': 'us',
    'il': 'us', 'in': 'us', 'ia': 'us', 'ks': 'us', 'ky': 'us', 'la': 'us',
    'me': 'us', 'md': 'us', 'ma': 'us', 'mi': 'us', 'mn': 'us', 'ms': 'us',
    'mo': 'us', 'mt': 'us', 'ne': 'us', 'nv': 'us', 'nh': 'us', 'nj': 'us',
    'nm': 'us', 'ny': 'us', 'nc': 'us', 'nd': 'us', 'oh': 'us', 'ok': 'us',
    'or': 'us', 'pa': 'us', 'ri': 'us', 'sc': 'us', 'sd': 'us', 'tn': 'us',
    'tx': 'us', 'ut': 'us', 'vt': 'us', 'va': 'us', 'wa': 'us', 'wv': 'us',
    'wi': 'us', 'wy': 'us',
    
  // Canadian provinces
  'ab': 'ca', 'bc': 'ca', 'mb': 'ca', 'nb': 'ca', 'nf': 'ca', 'ns': 'ca',
  'nt_ca': 'ca', 'nu': 'ca', 'on': 'ca', 'pe': 'ca', 'qc': 'ca', 'sk': 'ca',
  'yt': 'ca',
  
  // Australian states
  'nsw': 'au', 'vic': 'au', 'qld': 'au', 'wa_au': 'au', 'sa_au': 'au', 'tas': 'au',
  'act': 'au', 'nt_au': 'au'
  };
  
  const normalized = stateCode.toLowerCase();
  return stateToCountry[normalized] || 'us';
};